import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { formatUSD } from '@sparefoot/react-core/utils/format';

import { Paragraph, Text } from 'components/core/Typography';
import { Tooltip } from 'components/core/Tooltip';
import QuestionMark from 'components/svg/QuestionMark';
import { Button } from 'components/core/Button';
import { FeeWarningToolTipV2 } from 'components/unit/FeeWarningToolTipV2';
import { UnitIconV2 } from 'components/unit/UnitIconV2';
import { formatUnitSize } from 'utils/unitNameTrim';
import { createReserveClickHandler } from './reserveClickHandler';

import './BookableUnitDetailsV2.scss';

export function BookableUnitDetailsV2({ facility, unit, isNewSearchPage }) {
	const [showAmenities, setShowAmenities] = useState(false);
	const regularPrice = unit?.price?.regular;
	const salePrice = unit?.price?.meta;
	const showStrikethrough =
		isNewSearchPage && regularPrice && regularPrice > salePrice;

	const handleReserveClick = useMemo(
		() => createReserveClickHandler({ facility, unit }),
		[facility, unit],
	);

	return (
		<div className="bookable-unit-details-v2">
			<div className="unit-details">
				{!!unit.tierName && (
					<div className="tier-name">
						<Paragraph>{unit.tierName}</Paragraph>
						{unit.tierDescription && (
							<Tooltip
								content={
									<span style={{ color: '#000000' }}>
										{unit.tierDescription}
									</span>
								}
								size="medium"
								backgroundColor="white"
								className="tier-tooltip-no-border"
							>
								<span className="question-mark">
									<QuestionMark showTitle={false} />
								</span>
							</Tooltip>
						)}
					</div>
				)}
				<div className="unit-overview-container">
					<div className="unit-image-and-description">
						{/* Unit Image */}
						<div className="facility-unit-image">
							<UnitIconV2
								squareFootage={unit.squareFootage}
								type={unit.unitType}
								amenities={unit.amenities}
								alt={`Depiction of a ${unit.size} self-storage unit`}
							/>
						</div>
						<div className="facility-details-container">
							{!!unit.special && (
								<div className="unit-promotion-container">
									<Paragraph
										last
										size="tiny"
										className="unit-promotion-text"
										color="greenHero"
									>
										{unit.special}
									</Paragraph>
								</div>
							)}
							<div className="facility-unit-size">
								<Text
									last
									className="unit-size"
								>
									{formatUnitSize(unit.sizeText)}{' '}
									{unit?.typeText}
								</Text>

								{/* Unit Price Per Sqft */}
								{!!unit?.pricePerSqFt && (
									<Text className="price-per-sqft-content">
										$
										{unit?.pricePerSqFt.sparefoot ||
											unit?.pricePerSqFt.regular}{' '}
										/ sqft
									</Text>
								)}
							</div>
							<div className="amenities-list">
								{!isEmpty(unit?.availableAmenities) && (
									<ul className="amenities">
										{unit?.availableAmenities
											?.slice(0, 5)
											?.map((amenity, index, array) => (
												<React.Fragment key={amenity}>
													<li className="amenities-item">
														{amenity}
													</li>

													{index <
														array.length - 1 && (
														<li className="amenities-elipses-dot"></li>
													)}
												</React.Fragment>
											))}
										{unit?.availableAmenities?.length >
											5 && (
											<React.Fragment>
												<li className="amenities-elipses-dot"></li>
												<li className="amenities-item">
													<button
														type="button"
														className="more-amenities-btn"
														onClick={() =>
															setShowAmenities(
																!showAmenities,
															)
														}
													>
														+{' '}
														{unit
															?.availableAmenities
															.length - 5}{' '}
														Amenities
													</button>
												</li>
											</React.Fragment>
										)}
									</ul>
								)}
								{showAmenities && (
									<ul className="amenities-remaining-list">
										{unit?.availableAmenities
											?.slice(5)
											?.map((amenity, index, array) => (
												<React.Fragment key={amenity}>
													<li
														key={amenity}
														className="amenities-remaining-list-item"
													>
														{amenity}
													</li>
													{index <
														array.length - 1 && (
														<li className="amenities-elipses-gray-dot"></li>
													)}
												</React.Fragment>
											))}
									</ul>
								)}
							</div>
						</div>
					</div>

					<div className="facility-price-reserve-container">
						<div className="facility-unit-price">
							{showStrikethrough && (
								<Text
									color="placeholderGray"
									size="small"
									className="unit-price-strikethrough"
									strikethrough
									last
								>
									{formatUSD(regularPrice)}
								</Text>
							)}
							<Text
								last
								color="red"
								size="mediumLarge"
								className="unit-price"
							>
								{formatUSD(salePrice)}
							</Text>
							{facility?.showFeeWarning &&
								facility?.totalFees > 0 && (
									<FeeWarningToolTipV2
										facility={facility}
										className="fee-tooltip-v2"
									/>
								)}
							{unit.quantity && unit.quantity <= 3 && (
								<Text
									last
									className="unit-low-quantity"
								>
									ONLY {unit?.quantity} LEFT!
								</Text>
							)}
						</div>
						<div className="facility-unit-reserve-container">
							<div className="facility-unit-reserve">
								<Button
									className="reserve-button"
									to={unit.url}
									onClick={handleReserveClick}
								>
									Reserve Now, Pay Later
								</Button>
								<span className="reserve-button-bottom-border"></span>
							</div>
							<span className="reserve-information">
								You will not be charged until move-in
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

BookableUnitDetailsV2.propTypes = {
	facility: PropTypes.object.isRequired,
	unit: PropTypes.object.isRequired,
	isNewSearchPage: PropTypes.bool,
};

export default BookableUnitDetailsV2;
